/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectHotelName,
  selectTargetDate,
  selectVisualFilters,
  selectPmsSync,
  selectPaceHorizon,
  selectEnableHotelGroups,
  selectReportType,
  selectIsDemoUser,
  selectIsPrimaryHotelSetOverTime,
  selectInvalidPrimaryHotelList,
} from 'modules/dashboard/selectors';
import { PACE_HORIZON_SELECTION, REPORT_TYPE, TAGS } from 'modules/dashboard/constants';
import {
  getBookingDateSchema,
  getDashboardPmsSyncSchema,
  getGroupASchema,
  getGroupHotelFilterSchema,
  getHotelGroupASchema,
  getHotelGroupOrderSchema,
  getHotelGroupSchema,
  getHotelIdSchema,
  getIsForecastSchema,
  getNormalizedViewSchema,
  getPaceGranularitySchema,
  getPaceWidgetPaceHorizonSchema,
  getPmsSyncSchema,
  getRevenueForecastSchema,
  getRoomForecastSchema,
  getSegmentFocusOnSchema,
  getTargetDateSchema,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import { paceActions } from 'modules/dashboard/components/tab-container/pace-tabs/slice';
import {
  selectEnableSecondaryHotelGroups,
  selectIsPrimary,
  selectNormalizedView,
  selectPaceBookingDate,
  selectPaceBookingPaceWindow,
  selectPaceWidgetHotelFilterList,
  selectIsForecast,
  selectHotelFocusOn,
  selectTabularView,
  selectAdjustedStartDate,
  selectAdjustedEndDate,
  selectGranularity,
  selectPaceHotelTabularFilterList,
  selectPaceFocusOn,
  selectPaceChartBreakdown,
  selectIsPaceDetailed,
  selectIsFocusOnSet,
  selectIsSecondaryHotelSetOverTime,
  selectInvalidSecondaryHotelList,
} from 'modules/dashboard/components/tab-container/pace-tabs/selectors';
import writeLog from 'modules/common/utils/filter-log';
import { GRANULARITY } from '../constants';
import { getSelectedBreakdown } from '../../../../functions';

/**
 * Custom hook to generate summary widget filter schemas
 */
const usePaceHotelWidget = () => {
  const dispatch = useDispatch();
  //
  const targetDate = useSelector(selectTargetDate);
  const hotelId = useSelector(selectHotelName);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const paceHorizon = useSelector(selectPaceHorizon);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const paceHotelVisualFilterList = useSelector(selectPaceWidgetHotelFilterList);
  const isSegment = useSelector(selectIsPrimary);
  const reportType = useSelector(selectReportType);
  const bookingDate = useSelector(selectPaceBookingDate);
  const bookingPaceWindow = useSelector(selectPaceBookingPaceWindow);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const isPaceForecast = useSelector(selectIsForecast);
  const isDemoUser = useSelector(selectIsDemoUser);
  const normalizedViewToggle = useSelector(selectNormalizedView);
  const isTabular = useSelector(selectTabularView);
  const adjustedStartDate = useSelector(selectAdjustedStartDate);
  const adjustedEndDate = useSelector(selectAdjustedEndDate);
  const granularity = useSelector(selectGranularity);
  const tabularFilterList = useSelector(selectPaceHotelTabularFilterList);
  const focusOn = useSelector(selectPaceFocusOn);
  const chartBreakdown = useSelector(selectPaceChartBreakdown);
  const isDetailed = useSelector(selectIsPaceDetailed);
  const IsFocusOnSet = useSelector(selectIsFocusOnSet);
  const isPrimaryHotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  const isSecondaryHotelSetOverTime = useSelector(selectIsSecondaryHotelSetOverTime);
  const invalidSecondaryHotels = useSelector(selectInvalidSecondaryHotelList);

  /**
   * Sums all numeric fields across an array of objects, rounded to 2 decimal places.
   * @param {Array<Object>} data - Array of records.
   * @returns {Object} Totals for each numeric key, rounded.
   */

  // Generate filter list for pace segment visuals
  useEffect(() => {
    if (
      !IsFocusOnSet ||
      reportType !== REPORT_TYPE.PACE ||
      !paceHorizon ||
      !hotelId ||
      !visualFilters
    )
      return;

    const targetDateFilter = getTargetDateSchema(
      visualFilters,
      targetDate?.startDate,
      targetDate?.endDate
    );
    //
    let startDate;
    let endDate;
    if (isTabular) {
      if (granularity === GRANULARITY.DAILY) {
        startDate = bookingDate?.startDate;
        endDate = bookingDate?.endDate;
      } else {
        startDate = adjustedStartDate;
        endDate = adjustedEndDate;
      }
    } else {
      startDate = bookingDate?.startDate;
      endDate = bookingDate?.endDate;
    }
    const bookingDateFilter = getBookingDateSchema(visualFilters, startDate, endDate);
    //
    const paceHorizonFilter = getPaceWidgetPaceHorizonSchema(
      visualFilters,
      paceHorizon,
      bookingPaceWindow === PACE_HORIZON_SELECTION.BOOKING_PACE_HORIZON
    );
    //
    const pmsSyncFilter = getPmsSyncSchema(visualFilters, pmsSyncToggle, TAGS.PACE_WIDGET);
    const isForecastFilter = getIsForecastSchema(visualFilters, isPaceForecast);
    const hotelGroupOrderFilter = getHotelGroupOrderSchema(visualFilters, isDemoUser);
    const normalizedFilter = getNormalizedViewSchema(visualFilters, normalizedViewToggle);
    const roomForecastFilter = getRoomForecastSchema(visualFilters, pmsSyncToggle);
    const revenueForecastFilter = getRevenueForecastSchema(visualFilters, pmsSyncToggle);

    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);
    let hotelListIds = [];
    let hotelGroup1Filter;
    if (hotelId?.hotels) {
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }

    // Extract invalid secondary IDs
    const invalidSecondaryIds = invalidSecondaryHotels.map((hotel) => hotel.hotelCode);

    let groupFocusOnIds = [];
    if (hotelFocusOn?.hotels) {
      if (isSecondaryHotelSetOverTime) {
        groupFocusOnIds = hotelFocusOn.hotels
          .filter((hotel) => !invalidSecondaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        groupFocusOnIds = hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }
    //
    const groupHotelFilter = getGroupHotelFilterSchema(visualFilters);
    if (enableHotelGroups && hotelListIds?.length > 0) {
      hotelGroup1Filter = getHotelGroupSchema(hotelListIds, groupHotelFilter);
    } else {
      hotelGroup1Filter = getHotelGroupSchema([hotelId?.hotelCode], groupHotelFilter);
    }
    //
    const groupAFilter = getGroupASchema(visualFilters);
    let hotelGroupAFilter;
    if (enableSecondaryHotelGroups && groupFocusOnIds?.length > 0) {
      hotelGroupAFilter = getHotelGroupASchema(groupFocusOnIds, groupAFilter);
    } else {
      hotelGroupAFilter = getHotelGroupASchema([hotelFocusOn?.hotelCode], groupAFilter);
    }
    //
    let dimHotelIds = [];
    if (enableHotelGroups) {
      if (enableSecondaryHotelGroups) {
        dimHotelIds = new Set([...groupFocusOnIds, ...hotelListIds]);
      } else {
        dimHotelIds = new Set([hotelFocusOn?.hotelCode, ...hotelListIds]);
      }
    } else if (enableSecondaryHotelGroups) {
      dimHotelIds = new Set([...groupFocusOnIds, hotelId?.hotelCode]);
    } else {
      dimHotelIds = new Set([hotelFocusOn?.hotelCode, hotelId?.hotelCode]);
    }
    //
    const secondaryHotelIdFilter = getHotelIdSchema(Array.from(dimHotelIds), visualFilters);
    const hotelFilters = [secondaryHotelIdFilter, hotelGroupAFilter, hotelGroup1Filter];
    const granularityFilter = getPaceGranularitySchema(visualFilters, granularity);
    const selectedBreakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
    const segmentFocusOnFilter = getSegmentFocusOnSchema(
      selectedBreakdown,
      focusOn?.map((item) => item?.label)
    );
    const pmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    //
    if (
      targetDateFilter &&
      paceHorizonFilter &&
      pmsSyncFilter &&
      bookingDateFilter &&
      hotelFilters &&
      isForecastFilter &&
      hotelGroupOrderFilter &&
      normalizedFilter &&
      granularityFilter &&
      roomForecastFilter &&
      revenueForecastFilter &&
      segmentFocusOnFilter
    ) {
      const filters = [
        roomForecastFilter,
        revenueForecastFilter,
        targetDateFilter,
        paceHorizonFilter,
        ...pmsSyncFilter,
        bookingDateFilter,
        ...hotelFilters,
        isForecastFilter,
        hotelGroupOrderFilter,
        normalizedFilter,
        ...segmentFocusOnFilter,
        ...pmsSyncSchema,
      ];
      if (JSON.stringify(paceHotelVisualFilterList) !== JSON.stringify(filters)) {
        //
        writeLog('Pace Hotel Filters', filters);
        dispatch(paceActions.setPaceWidgetHotelFilterList(filters));
      }
      // tabular filter list
      const commonTabularFilterList = [
        targetDateFilter,
        paceHorizonFilter,
        ...pmsSyncFilter,
        bookingDateFilter,
        ...hotelFilters,
        hotelGroupOrderFilter,
        normalizedFilter,
        granularityFilter,
        ...segmentFocusOnFilter,
        isForecastFilter,
        ...pmsSyncSchema,
      ];
      if (JSON.stringify(tabularFilterList) !== JSON.stringify(commonTabularFilterList)) {
        writeLog('Pace Hotel Tabular Filters', commonTabularFilterList);
      }
      dispatch(paceActions.setPaceHotelTabularFilterList(commonTabularFilterList));
    }
  }, [
    pmsSyncToggle,
    visualFilters,
    targetDate,
    hotelId,
    paceHorizon,
    enableHotelGroups,
    reportType,
    isSegment,
    bookingDate,
    bookingPaceWindow,
    hotelFocusOn,
    enableSecondaryHotelGroups,
    isPaceForecast,
    isDemoUser,
    normalizedViewToggle,
    isTabular,
    adjustedStartDate,
    adjustedEndDate,
    granularity,
    focusOn,
    chartBreakdown,
    isDetailed,
    IsFocusOnSet,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
    isSecondaryHotelSetOverTime,
    invalidSecondaryHotels,
  ]);
};
//
export default usePaceHotelWidget;
